// #127 Sevkiyat Alanı haritası — react-leaflet + OpenStreetMap.
// Distribütör müşterilerini nokta olarak gösterir; kullanıcı "kalemle çizer gibi"
// serbest (freehand) bir veya birden çok dağınık poligon çizerek sevkiyat alanını
// belirler. Kriterlere uyan müşteriler vurgulanır; alan çizilince alan içindekiler
// ayrıca öne çıkarılır ve anlık sayı gösterilir. Mahalle/sokak detayında seçim için
// scrollWheelZoom + tam zoom serbesttir.
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';

const DEFAULT_CENTER = [39.0, 35.0]; // Türkiye geneli (scope yoksa)
const DEFAULT_ZOOM = 6;

// Ray-casting nokta-poligon testi. ring: [[lat,lng], ...]
export function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i][0], xi = ring[i][1];
    const yj = ring[j][0], xj = ring[j][1];
    const intersect = ((yi > lat) !== (yj > lat)) &&
      (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function pointInAnyPolygon(lat, lng, polygons) {
  if (!polygons || polygons.length === 0) return false;
  return polygons.some((ring) => ring.length >= 3 && pointInPolygon(lat, lng, ring));
}

// Haritayı verilen noktaları kapsayacak şekilde yakınlaştırır (ilk yüklemede).
function FitBounds({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    done.current = true;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 14); return; }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

// Serbest (freehand) çizim katmanı — drawing açıkken fareyle basılı tutup sürükleyerek
// poligon çizdirir; bırakınca onComplete(ring) ile tamamlanır. Map sürüklemesi geçici kapatılır.
function FreehandLayer({ drawing, onComplete }) {
  const map = useMap();
  const ptsRef = useRef([]);
  const lineRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    const container = map.getContainer();
    if (!drawing) {
      map.dragging.enable();
      container.style.cursor = '';
      return undefined;
    }
    map.dragging.disable();
    map.doubleClickZoom.disable();
    container.style.cursor = 'crosshair';

    const start = (e) => {
      activeRef.current = true;
      ptsRef.current = [[e.latlng.lat, e.latlng.lng]];
      if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
      lineRef.current = L.polyline(ptsRef.current, { color: '#2563eb', weight: 3, dashArray: '4 4' }).addTo(map);
    };
    const move = (e) => {
      if (!activeRef.current) return;
      ptsRef.current.push([e.latlng.lat, e.latlng.lng]);
      if (lineRef.current) lineRef.current.setLatLngs(ptsRef.current);
    };
    const end = () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      const pts = ptsRef.current.slice();
      if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
      ptsRef.current = [];
      if (pts.length >= 3) onComplete(pts);
    };

    map.on('mousedown', start);
    map.on('mousemove', move);
    map.on('mouseup', end);
    return () => {
      map.off('mousedown', start);
      map.off('mousemove', move);
      map.off('mouseup', end);
      if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
      map.dragging.enable();
      map.doubleClickZoom.enable();
      container.style.cursor = '';
    };
  }, [drawing, map, onComplete]);

  return null;
}

export default function ShipmentAreaMap({
  customers = [],
  matchedIds = null,      // Set<id> — kritere uyanlar (null = kriter yok, hepsi uyar)
  polygons = [],
  onPolygonsChange,
  height = 460,
}) {
  const [drawing, setDrawing] = useState(false);

  const valid = useMemo(
    () => customers.filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng)),
    [customers],
  );

  const hasArea = polygons && polygons.length > 0;

  // Sınıflandırma: alan içinde mi? kritere uyuyor mu?
  const stats = useMemo(() => {
    let matched = 0, inArea = 0, selected = 0;
    for (const c of valid) {
      const isMatched = !matchedIds || matchedIds.has(c.id);
      const isInArea = hasArea ? pointInAnyPolygon(c.lat, c.lng, polygons) : false;
      if (isMatched) matched++;
      if (isInArea) inArea++;
      if (isMatched && (!hasArea || isInArea)) selected++;
    }
    return { matched, inArea, selected, total: valid.length };
  }, [valid, matchedIds, polygons, hasArea]);

  const colorFor = (c) => {
    const isMatched = !matchedIds || matchedIds.has(c.id);
    const isInArea = hasArea ? pointInAnyPolygon(c.lat, c.lng, polygons) : false;
    if (isMatched && (!hasArea || isInArea)) return '#16a34a'; // seçili (yeşil)
    if (isMatched && hasArea) return '#fdab3d';                // kritere uyar ama alan dışı (amber)
    return '#cbd5e1';                                          // kapsam dışı (gri)
  };

  const addPolygon = (ring) => onPolygonsChange?.([...(polygons || []), ring]);
  const removeLast = () => onPolygonsChange?.((polygons || []).slice(0, -1));
  const clearAll = () => onPolygonsChange?.([]);

  return (
    <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)' }}>
      {/* Çizim araç çubuğu — sağ-üst köşe (Leaflet zoom +/- sol-üstte kalsın, çakışmasın) */}
      <div style={{
        position: 'absolute', top: 10, right: 10, zIndex: 1000,
        display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end',
      }}>
        <button type="button" onClick={() => setDrawing((d) => !d)} style={toolBtn(drawing)}>
          {drawing ? '✓ Çizimi Bitir' : '✏️ Alan Çiz'}
        </button>
        <button type="button" onClick={removeLast} disabled={!hasArea} style={toolBtn(false, !hasArea)}>
          Son Alanı Sil
        </button>
        <button type="button" onClick={clearAll} disabled={!hasArea} style={toolBtn(false, !hasArea)}>
          Tümünü Temizle
        </button>
      </div>

      {/* Sayaç rozeti — sağ-alt köşe (üstte araç çubuğuyla çakışmasın) */}
      <div style={{
        position: 'absolute', bottom: 10, right: 10, zIndex: 1000,
        background: 'rgba(255,255,255,0.94)', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '8px 12px', fontSize: 12, color: '#334155', boxShadow: '0 2px 8px rgba(15,23,42,0.12)',
        lineHeight: 1.5, minWidth: 150,
      }}>
        <div><b style={{ color: '#16a34a' }}>{stats.selected}</b> seçili müşteri</div>
        <div style={{ color: '#64748b' }}>Kritere uyan: {stats.matched}{hasArea ? ` · Alan içinde: ${stats.inArea}` : ''}</div>
        <div style={{ color: '#94a3b8' }}>Kapsam: {stats.total} müşteri</div>
      </div>

      {drawing && (
        <div style={{
          position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
          background: 'rgba(37,99,235,0.92)', color: '#fff', borderRadius: 999, padding: '6px 14px',
          fontSize: 12, fontWeight: 600, pointerEvents: 'none',
        }}>
          Fareyle basılı tutup alanı çizin — birden çok dağınık alan çizebilirsiniz.
        </div>
      )}

      <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height, width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={valid} />
        <FreehandLayer drawing={drawing} onComplete={addPolygon} />

        {(polygons || []).map((ring, i) => ring.length >= 3 && (
          <Polygon key={i} positions={ring} pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.12 }} />
        ))}

        {valid.map((c, i) => (
          <CircleMarker
            key={c.id ?? i}
            center={[c.lat, c.lng]}
            radius={5}
            pathOptions={{ color: '#fff', weight: 1, fillColor: colorFor(c), fillOpacity: 0.95 }}
          >
            <Tooltip direction="top">
              {c.code ? <span style={{ color: '#64748b' }}>{c.code} · </span> : null}
              {c.label}
              {(c.neighborhood || c.street) ? <div style={{ color: '#94a3b8', fontSize: 11 }}>{[c.neighborhood, c.street].filter(Boolean).join(' / ')}</div> : null}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      {valid.length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.72)', color: 'var(--text-secondary, #6b7280)', fontSize: 13,
          pointerEvents: 'none', zIndex: 500,
        }}>
          Distribütör kapsamında konumu olan müşteri bulunamadı.
        </div>
      )}
    </div>
  );
}

function toolBtn(active, disabled) {
  return {
    background: active ? '#2563eb' : 'rgba(255,255,255,0.95)',
    color: active ? '#fff' : '#334155',
    border: '1px solid ' + (active ? '#2563eb' : '#cbd5e1'),
    borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    boxShadow: '0 1px 4px rgba(15,23,42,0.12)',
  };
}
